<script setup lang="ts">
import SearchFormContent from '@/components/UserRoles/SearchFormContent.vue';
import { userRoleSearchFormType } from "@/types/requests/userRoleSearchForm"
import UserRoleService from "@/services/models/UserRole";
import SelectRoleChecker from '@/router/selectRoleChecker'
import { OPERATION_OFFICE, DEPARTMENT } from "@/services/consts/user_role";
import { Ref, ref, computed, watch } from 'vue';
import i18n from '@/lang/i18n';

const props = defineProps<{
  isLoading: boolean
  errors: {organization: string[], department: string[], display_name: string[]}
}>()

const emits = defineEmits([
    'search',
    'setErrors',
    'clear'
])
const hide: Ref<boolean> = ref(false)
const form = ref({} as userRoleSearchFormType)
const searchFormContent = ref()

const unwatchOrganization = watch(
  () => form.value.organization,
  () => {
    const current_role = SelectRoleChecker.getCurrentRole()
    if(current_role && current_role.organization_type == OPERATION_OFFICE.type){
      emits('search', form.value)
    }
    unwatchOrganization();
  },
  {flush: 'post'}
);

const unwatchDepartment = watch(
  () => form.value.department,
  () => {
    const current_role = SelectRoleChecker.getCurrentRole()
    if(current_role && current_role.organization_type == DEPARTMENT.type){
      emits('search', form.value)
    }
    unwatchDepartment();
  },
  {flush: 'post'}
);

const title = computed(() => {
  return hide.value ? "" : i18n.global.t('pages.common.filter.title')
})

const searchAreaClass = computed(() => {
  return hide.value ? "search-area-hide p-4" : "search-area p-4"
})

function clearForm () {
  form.value = {
    organization: { id: null, type: "", name: "", departments: []},
    department: { id: null,operation_office_id: null, name: ""},
    display_name: ""
  }
  searchFormContent.value.resetOfficeableType()
  clearErrors()
  searchFormContent.value.clearSessionStorage('userRolesSearchFormContent')
  emits('clear')
}

const changeSearchButton = computed(() => {
  return props.isLoading ? i18n.global.t('pages.common.button.loading') : i18n.global.t('pages.common.button.exec')
})

function handleClickSearch() {
  clearErrors()
  if (validateForm(form.value)) {
    emits('search', form.value)
    searchFormContent.value.saveFormValuesToSessionStorage()
  }
}

function validateForm(form: userRoleSearchFormType) {
  let ret = true
  const errors = UserRoleService.validateSearchForm(form)

  if(errors.organization.length > 0) {
    ret = false
  }
  if(errors.department.length > 0) {
    ret = false
  }
  if(errors.display_name.length > 0) {
    ret = false
  }
  if(!ret) {
    emits('setErrors', errors)
  }

  return ret
}

function toggleSidebar () {
  hide.value = !hide.value
}

function clearErrors() {
  const errors = {
    organization: [],
    department: [],
    display_name: []
  }
  emits('setErrors', errors)
}

</script>
<template>
  <div :class="searchAreaClass">
    <h4 class="d-flex justify-content-between mt-0">
      {{ title }}
      <svg
        width="24px"
        height="24px"
        viewBox="0 0 24 24"
        fit=""
        preserveAspectRatio="xMidYMid meet"
        focusable="false"
        sandboxuid="0"
        @click="toggleSidebar"
      >
        <path v-if="hide" d="M7.615 11.795l4.59-4.59-1.41-1.41-6 6 6 6 1.41-1.41-4.59-4.59zm9.59 6v-12h-2v12h2z" fill-rule="evenodd" sandboxuid="0"></path>
        <path v-if="!hide" d="M6 7.41L10.438 12 6 16.59 7.363 18l5.802-6-5.802-6L6 7.41zM16.066 6H18v12h-1.934V6z" sandboxuid="0" />
      </svg>
    </h4>
    <div :class="`strpied-tabled-with-hover ${hide ? 'd-none' : '' }`">
      <div class="d-grid gap-2">
        <SearchFormContent
          ref="searchFormContent"
          v-model="form"
          :errors="errors"
        />
      </div>
      <div class="d-flex flex-column">
        <div class="col-md-3">
          <svg
            version="1.2"
            style="overflow: visible; width: 299px; height: 1px; opacity: 1; mix-blend-mode: normal; rotate: 0deg;"
          >
            <defs>
              <path id="path-1650848226989195" d="M1126 703 C1126 703 1425 703 1425 703"></path>
            </defs>
            <g transform="translate(-1126, -703)">
              <path style="stroke: rgb(194, 186, 186); stroke-width: 1; stroke-linecap: butt; stroke-linejoin: miter; fill: none;" d="M1126 703 C1126 703 1425 703 1425 703"></path>
            </g>
          </svg>
        </div>
        <div class="d-flex search-buttons mx-auto" >
          <v-btn
            class="ma-2 exec-btn"
            @click="handleClickSearch"
            :disabled="props.isLoading"
            :style="{
              backgroundColor : props.isLoading ? '#B3B8BB' : ''
            }"
          >
            {{ changeSearchButton }}
          </v-btn>
          <v-btn
            class="ma-2 reset-btn"
            :disabled="props.isLoading"
            @click="clearForm"
          >
            {{ i18n.global.t('pages.common.button.reset') }}
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>
